import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { studioApi, idempotencyKey } from '../data/studioApi';
import useStableActions from './useStableActions';

const RUNNING = ['queued', 'running'];
// A failed video that can pass on a new mapping as it is (a momentary download or
// transcription failure). The others have to be swapped.
const RETRYABLE = ['download_failed', 'provider_unavailable'];
const idle = { profileId: null, status: 'idle', record: null };
// The covers of the personality's and the mapping's sources, and of the reels the comparison by
// reach used (2.5.0), are signed copies that expire; a record whose covers expire within a
// minute is read again to renew them.
const stale = record => [...(record?.personality?.sources || []), ...(record?.personality?.performance?.videos || []), ...(record?.mapping?.sources || [])]
  .some(source => source.preview?.timestamps?.expires && Date.parse(source.preview.timestamps.expires) - Date.now() < 60e3);

// What a failed mapping leaves for the next one (contract 2.3.0): its videos in their
// places, the ones that worked kept (their transcripts are reused, not charged again) and
// the ones to swap marked with why. A failed video whose cause is momentary stays as it is.
// Null when the latest mapping did not fail.
export const retryOf = mapping => {
  if (mapping?.status !== 'failed') return null;
  const slots = mapping.sources.map(source => ({
    video: { id: source.video_id, caption: source.caption, preview: source.preview, duration_seconds: source.duration_seconds, permalink: source.permalink },
    reason: source.status === 'failed' && !RETRYABLE.includes(source.error_code) ? source.error_code || 'video_unavailable' : null,
  }));
  return { mappingId: mapping.id, code: mapping.error?.code || null, slots, swap: slots.filter(slot => slot.reason).length };
};

// Personality (ClickUp 868mhqewg, contract 2.3.0, `features.personality`): the active
// profile's personality, the one its scripts keep, plus the latest mapping. It is read when
// the profile changes and polled while a mapping runs. A new mapping (3 Reels and a
// `personality` quote) never clears the personality in use: while it runs, and if it fails,
// the previous one stays. After a failure, `retry` says which videos to keep and which to
// swap. The picker (dialogs/PersonalityDialog) opens from the composer; after an onboarding
// it opens once, while the new profile has none. Skipping it stores nothing (D8).
//
// status: idle (feature off or no profile) · loading · ready · error · off (`personality_unavailable`)
export default function useMyPersonality({ features, profileId, pollMs, setModal, refreshUsage, mounted }) {
  const enabled = !!features.personality && !!profileId;
  const [state, setState] = useState(idle);
  const scope = useRef(profileId);
  scope.current = profileId;
  // Kept while a start fails on the network, so retrying it cannot open a second mapping.
  const startKey = useRef(null);

  // A read for a profile that is no longer active is dropped.
  const load = useCallback(async id => {
    try {
      const record = await studioApi.profilePersonality(id);
      if (mounted.current && scope.current === id) setState({ profileId: id, status: 'ready', record });
      return record;
    } catch (failure) {
      if (mounted.current && scope.current === id) {
        setState(previous => ({ profileId: id, status: failure.code === 'personality_unavailable' ? 'off' : 'error', record: previous.profileId === id ? previous.record : null }));
      }
      return null;
    }
  }, [mounted]);
  useEffect(() => {
    if (!enabled) { setState(idle); return; }
    setState({ profileId, status: 'loading', record: null });
    load(profileId);
  }, [enabled, profileId, load]);

  const record = enabled && state.profileId === profileId ? state.record : null;
  const mapping = record?.mapping || null;
  const running = !!mapping && RUNNING.includes(mapping.status);
  // While it runs, every `poll_interval_seconds`. Once it settles the balance moved: charged
  // on success, released on failure.
  useEffect(() => {
    if (!running) return undefined;
    const id = profileId;
    let stopped = false;
    let timer = null;
    const tick = async () => {
      const next = await load(id);
      if (stopped) return;
      if (next && !RUNNING.includes(next.mapping?.status)) { refreshUsage(); return; }
      timer = setTimeout(tick, pollMs);
    };
    timer = setTimeout(tick, pollMs);
    return () => { stopped = true; clearTimeout(timer); };
  }, [running, mapping?.id, profileId, pollMs, load, refreshUsage]); // eslint-disable-line react-hooks/exhaustive-deps

  // The ceiling of a mapping of exactly these videos; it reserves nothing yet. Videos the
  // backend transcribed in the last 24 h are not in it.
  const quote = videoIds => studioApi.personalityQuote({ kind: 'personality', profile_id: profileId, video_ids: videoIds }, idempotencyKey('personality-quote'));
  // Starts the mapping the creator consented to. An expired quote is quoted again once.
  // Failures are thrown: the picker shows them, and `insufficient_credits` opens the plans.
  const start = async (videoIds, quoted) => {
    const id = profileId;
    const begin = usageQuoteId => {
      startKey.current = startKey.current || idempotencyKey('personality');
      return studioApi.startPersonalityMapping(id, { video_ids: videoIds, usage_quote_id: usageQuoteId }, startKey.current);
    };
    let started;
    try {
      started = await begin(quoted.id).catch(async failure => {
        if (failure.code !== 'usage_quote_expired') throw failure;
        startKey.current = null;
        return begin((await quote(videoIds)).id);
      });
      startKey.current = null;
    } catch (failure) {
      if (failure.code !== 'network') startKey.current = null;
      throw failure;
    }
    if (mounted.current && scope.current === id) setState(previous => ({ ...previous, status: 'ready', record: { ...previous.record, status: 'mapping', mapping: started } }));
    refreshUsage();
    return started;
  };
  const open = (origin = 'composer') => {
    if (!enabled) return;
    if (stale(record)) load(profileId);
    setModal({ type: 'personality', origin });
  };
  // The end of an onboarding: the new profile without a personality meets the picker, once.
  const offer = async () => {
    setModal(null);
    if (!enabled) return false;
    const id = profileId;
    const current = await load(id);
    if (current?.status !== 'not_configured' || !mounted.current || scope.current !== id) return false;
    setModal({ type: 'personality', origin: 'onboarding' });
    return true;
  };

  const actions = useStableActions({ quote, start, open, offer, refresh: () => (profileId ? load(profileId) : Promise.resolve(null)) });
  const status = enabled ? (state.profileId === profileId ? state.status : 'loading') : 'idle';
  const retry = useMemo(() => retryOf(mapping), [mapping]);
  // One identity while nothing changed: the composer is memoized on `myPersonality`.
  return useMemo(() => ({ enabled: enabled && status !== 'off', status, record, mapping, running, retry, personality: record?.personality || null, ...actions }),
    [enabled, status, record, mapping, running, retry, actions]);
}
